import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, AlertCircle } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { showToast } from '@/components/ui/Toast';
import Button from '@/components/ui/Button';
import DuplicateReviewSlider from './DuplicateReviewSlider';
import LeadDetailsSidebar from '@/components/LeadDetails/LeadDetailsSidebar';
import { Lead } from '@/types/leads/Table';
import { useUsers } from '@/contexts/UsersContext';
import { format } from 'date-fns';

interface UserOption {
  value: string;
  label: string;
  email?: string;
  role?: string;
}

interface DuplicateLead {
  uuid: string;
  lead_id: number;
  name: string;
  email: string;
  phone: string;
  message?: string;
  source?: number;
  status?: number;
  created_date: string;
  last_updated_date?: string;
  duplicate_count?: number;
  sourceDisplay?: string;
  statusDisplay?: string;
  sourceOption?: {
    opt_name: string;
  };
  statusOption?: {
    opt_name: string;
  };
  // UTM fields
  utm_source?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  utm_location?: string | null;
  utm_device?: string | null;
  utm_network?: string | null;
  utm_platform?: string | null;
  utm_creative?: string | null;
  utm_adposition?: string | null;
  // Facebook fields
  leadgen_id?: string | null;
  form_id?: string | null;
  form_name?: string | null;
  fb_platform?: string | null;
  fb_ad_id?: string | null;
  fb_ad_name?: string | null;
  fb_adset_id?: string | null;
  fb_adset_name?: string | null;
  fb_campaign_id?: string | null;
  fb_campaign_name?: string | null;
  fb_is_organic?: boolean | null;
  // LinkedIn fields
  li_campaign_name?: string;
  li_campaign_id?: string | null;
  li_form_name?: string;
  li_form_id?: string | null;
  // Google tracking
  gclid?: string;
  fclid?: string;
}

const Duplicates: React.FC = () => {
  const { activeUsers } = useUsers();
  const [leads, setLeads] = useState<DuplicateLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [isSliderOpen, setIsSliderOpen] = useState(false);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    pageSize: 25
  });

  const [sortBy, setSortBy] = useState('created_date');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  // Transform active users from context to dropdown format
  const activeUsersForDropdown: UserOption[] = useMemo(() => {
    return activeUsers.map((u) => ({
      value: u.uuid,
      label: u.userName,
      email: u.userEmail,
      role: u.role?.role_name
    }));
  }, [activeUsers]);

  const fetchDuplicates = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
        primary_filters: {}
      };

      const response = await apiRequest('/api/duplicates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.success) {
        console.log('📥 Duplicates API response sample:', response.data.leads[0]);
        setLeads(response.data.leads);
        setPagination(prev => ({
          ...prev,
          totalPages: response.data.pagination.totalPages,
          total: response.data.pagination.total
        }));
      } else {
        showToast(response.message || 'Failed to fetch duplicates', 'error');
      }
    } catch (error: any) {
      showToast(error.message || 'Failed to fetch duplicates', 'error');
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.pageSize, sortBy, sortOrder]);

  useEffect(() => {
    fetchDuplicates();
  }, [fetchDuplicates]);

  const handlePhoneClick = (phone: string) => {
    setSelectedPhone(phone);
    setIsSliderOpen(true);
  };

  const handleCloseSlider = () => {
    setIsSliderOpen(false);
    setSelectedPhone(null);
  };

  const handleReviewComplete = async () => {
    // Refresh duplicates list
    await fetchDuplicates();
  };

  const handleEditLead = (lead: Lead) => {
    setSelectedLead(lead);
    setIsLeadModalOpen(true);
  };

  const closeLeadModal = () => {
    setIsLeadModalOpen(false);
    setSelectedLead(null);
  };

  const handleLeadUpdate = async () => {
    console.log('🔄 Lead updated, refreshing duplicates...');
    // Close the lead modal
    closeLeadModal();
    // Refresh duplicates list
    await fetchDuplicates();
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('DESC');
    }
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  return (
    <div className="w-full h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0 sticky top-0 z-50">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-gray-900">Duplicates</h1>
              <span className="text-sm font-medium text-gray-700">
                <span className="font-bold text-gray-900">{pagination.total}</span> duplicate phone numbers
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="flex-1 overflow-hidden">
        <div className="bg-white h-full flex flex-col">
          {/* Table */}
          <div className="flex-1 overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : leads.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <AlertCircle className="h-12 w-12 mb-3 text-gray-400" />
                <p className="text-lg font-medium">No duplicate leads found</p>
                <p className="text-sm mt-1">All leads are unique or duplicates have been reviewed</p>
              </div>
            ) : (
              <table className="w-full border-collapse">
                <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                  <tr>
                    <th
                      className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('phone')}
                    >
                      Phone {sortBy === 'phone' && (sortOrder === 'ASC' ? '↑' : '↓')}
                    </th>
                    <th
                      className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('name')}
                    >
                      Name {sortBy === 'name' && (sortOrder === 'ASC' ? '↑' : '↓')}
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      Email
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      Source
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      Status
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      Dup Count
                    </th>
                    <th
                      className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('created_date')}
                    >
                      Created Date {sortBy === 'created_date' && (sortOrder === 'ASC' ? '↑' : '↓')}
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      UTM Source
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      UTM Campaign
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      UTM Content
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      UTM Term
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      UTM Location
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      FB Campaign
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      FB Ad Name
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      FB AdSet
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      FB Platform
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      Form Name
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      LI Campaign
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      LI Form
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      GCLID
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead, rowIndex) => (
                    <tr
                      key={lead.uuid}
                      className={`hover:bg-blue-50/50 cursor-pointer transition-colors border-b border-gray-100 ${
                        rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                      }`}
                      onClick={() => handlePhoneClick(lead.phone)}
                    >
                      <td className="px-4 py-1.5 whitespace-nowrap border-r border-gray-200">
                        <span className="text-sm font-bold text-gray-900">{lead.phone}</span>
                      </td>
                      <td className="px-4 py-1.5 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">
                        {lead.name || '-'}
                      </td>
                      <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-700 border-r border-gray-200">
                        {lead.email || '-'}
                      </td>
                      <td className="px-4 py-1.5 whitespace-nowrap border-r border-gray-200">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {lead.sourceDisplay || lead.sourceOption?.opt_name || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-1.5 whitespace-nowrap border-r border-gray-200">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {lead.statusDisplay || lead.statusOption?.opt_name || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-1.5 whitespace-nowrap text-center border-r border-gray-200">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold bg-red-100 text-red-700">
                          {lead.duplicate_count || 0}
                        </span>
                      </td>
                      <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 border-r border-gray-200">
                        {format(new Date(lead.created_date), 'MMM dd, yyyy hh:mm a')}
                      </td>
                      <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 border-r border-gray-200">
                        {lead.utm_source || '-'}
                      </td>
                      <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 border-r border-gray-200">
                        {lead.utm_campaign || '-'}
                      </td>
                      <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 border-r border-gray-200">
                        {lead.utm_content || '-'}
                      </td>
                      <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 border-r border-gray-200">
                        {lead.utm_term || '-'}
                      </td>
                      <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 border-r border-gray-200">
                        {lead.utm_location || '-'}
                      </td>
                      <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 border-r border-gray-200">
                        {lead.fb_campaign_name || '-'}
                      </td>
                      <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 border-r border-gray-200">
                        {lead.fb_ad_name || '-'}
                      </td>
                      <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 border-r border-gray-200">
                        {lead.fb_adset_name || '-'}
                      </td>
                      <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 border-r border-gray-200">
                        {lead.fb_platform || '-'}
                      </td>
                      <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 border-r border-gray-200">
                        {lead.form_name || '-'}
                      </td>
                      <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 border-r border-gray-200">
                        {lead.li_campaign_name || '-'}
                      </td>
                      <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 border-r border-gray-200">
                        {lead.li_form_name || '-'}
                      </td>
                      <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 border-r border-gray-200">
                        {lead.gclid || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {!loading && leads.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Page {pagination.currentPage} of {pagination.totalPages}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.currentPage === 1}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-3 text-sm text-gray-700">
                  {pagination.currentPage}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={pagination.currentPage === pagination.totalPages}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Duplicate Review Slider */}
      {selectedPhone && (
        <DuplicateReviewSlider
          isOpen={isSliderOpen}
          onClose={handleCloseSlider}
          phone={selectedPhone}
          onReviewComplete={handleReviewComplete}
          onEditLead={handleEditLead}
        />
      )}

      {/* Lead Edit Modal */}
      <LeadDetailsSidebar
        isOpen={isLeadModalOpen}
        onClose={closeLeadModal}
        lead={selectedLead}
        activeUsersForDropdown={activeUsersForDropdown}
        onUpdate={handleLeadUpdate}
      />
    </div>
  );
};

export default Duplicates;
